// compliance-friendly-outreach.js - Tier 1 Educational
export const content = {
  heroStyle: 'dark',
  tag: 'Compliance',

  html: `
<p>Retention outreach is only valuable if it keeps you on the right side of Medicare marketing rules. CMS has clear guidelines about how agents can communicate with beneficiaries, and violating them can cost you your ability to sell Medicare products altogether.</p>

<p>The good news: compliant outreach and effective retention aren't in conflict. You can stay in close contact with clients while following every rule. Here's how.</p>

<h2>CMS Marketing Rule Basics</h2>

<p>The Medicare Communications and Marketing Guidelines (MCMG) govern how you can communicate with beneficiaries. Key principles:</p>

<ul>
<li><strong>Beneficiary permission:</strong> Generally need permission for marketing communications</li>
<li><strong>No misleading information:</strong> Everything must be accurate and not deceive</li>
<li><strong>Enrollment period rules:</strong> Different rules apply during different periods</li>
<li><strong>Scope of appointment:</strong> Must document what you're meeting to discuss</li>
<li><strong>Record keeping:</strong> Maintain documentation of communications</li>
</ul>

<h2>Marketing vs. Educational Communications</h2>

<p>CMS distinguishes between marketing and educational communications:</p>

<h3>Marketing Communications</h3>
<p>Communications intended to influence enrollment decisions or promote a specific plan. Requires beneficiary consent (written or recorded verbal).</p>

<h3>Educational Communications</h3>
<p>General information about Medicare programs, coverage options, and how Medicare works—without promoting a specific plan. Less restrictive.</p>

<p>Many retention communications can be educational rather than marketing, which gives you more flexibility.</p>

<h2>Compliant Retention Outreach</h2>

<h3>Service-Oriented Contact</h3>
<p>Reaching out about their existing coverage is service, not marketing:</p>
<ul>
<li>"I'm calling to check how your coverage is working"</li>
<li>"I noticed your plan has some changes next year—wanted to explain them"</li>
<li>"Just following up to make sure your doctor visits are going smoothly"</li>
</ul>

<p>This is serving your existing client, which is expected and encouraged.</p>

<h3>Birthday and Anniversary Acknowledgments</h3>
<p>Personal acknowledgments that don't promote products are fine:</p>
<ul>
<li>"Happy birthday! Hope you have a great day."</li>
<li>"It's been a year since we got your coverage set up—just wanted to say thanks for trusting me."</li>
</ul>

<p>Keep it personal, not promotional.</p>

<h3>Educational Information</h3>
<p>Sharing general Medicare information is educational:</p>
<ul>
<li>"Annual enrollment is coming up—here's what that means and when to think about it"</li>
<li>"Medicare is making some changes—here's what you should know"</li>
<li>"Did you know your plan includes a flu shot at no cost?"</li>
</ul>

<p>Educational communications help clients without crossing into marketing territory.</p>

<h3>Plan Change Notifications</h3>
<p>Proactively informing clients about changes to their plan is service:</p>
<ul>
<li>"Your plan's formulary is changing—wanted to make sure you know before it affects you"</li>
<li>"I saw that Dr. Smith is leaving the network—let me help you figure out next steps"</li>
</ul>

<h2>Scope of Appointment</h2>

<h3>What Requires SOA</h3>
<p>A Scope of Appointment is required before discussing Medicare plan options in person or via phone:</p>
<ul>
<li>Scheduled appointments to review coverage</li>
<li>Meetings where plan comparisons will occur</li>
<li>Discussions about changing plans</li>
</ul>

<h3>What Doesn't Require SOA</h3>
<ul>
<li>Quick service calls ("how do I use my benefits?")</li>
<li>Informational inquiries that don't involve plan selection</li>
<li>Check-in calls that stay service-focused</li>
</ul>

<p>When in doubt, get an SOA. It protects you and documents the client's consent.</p>

<h2>Documentation Best Practices</h2>

<h3>What to Document</h3>
<ul>
<li>Date, time, and method of communication</li>
<li>Purpose of the contact</li>
<li>Topics discussed</li>
<li>Outcomes and follow-up items</li>
<li>SOA if applicable</li>
</ul>

<h3>Why Documentation Matters</h3>
<ul>
<li>Demonstrates compliance if audited</li>
<li>Protects you from beneficiary complaints</li>
<li>Creates history for future interactions</li>
<li>Required by many carriers</li>
</ul>

<h2>Annual Enrollment Period Considerations</h2>

<p>During AEP (October 15 - December 7), marketing rules are strictly enforced:</p>

<h3>Do:</h3>
<ul>
<li>Conduct annual reviews with proper SOA</li>
<li>Provide objective plan comparisons</li>
<li>Educate about coverage options</li>
<li>Document everything</li>
</ul>

<h3>Don't:</h3>
<ul>
<li>Cold call or make unsolicited marketing calls</li>
<li>Use high-pressure tactics</li>
<li>Make misleading comparisons</li>
<li>Promise benefits that don't exist</li>
</ul>

<h2>Template Language Considerations</h2>

<p>When using templates for outreach:</p>

<h3>Compliant Template Elements</h3>
<ul>
<li>Service-focused language</li>
<li>Accurate information</li>
<li>No specific plan promotion without consent</li>
<li>Clear identification of who you are</li>
<li>Opt-out mechanism for marketing</li>
</ul>

<h3>Review Templates Regularly</h3>
<ul>
<li>Marketing rules change—update templates accordingly</li>
<li>Have compliance review before deployment</li>
<li>Err on the side of caution</li>
</ul>

<h2>Common Compliance Mistakes</h2>

<h3>Mistake: Unsolicited marketing calls</h3>
<p><strong>Fix:</strong> Only market to beneficiaries who have given permission. Service calls to existing clients are different from marketing calls.</p>

<h3>Mistake: Missing SOA documentation</h3>
<p><strong>Fix:</strong> Get SOA before any plan discussion meeting. Document and retain.</p>

<h3>Mistake: Misleading comparisons</h3>
<p><strong>Fix:</strong> Comparisons must be accurate and complete. Don't cherry-pick favorable stats.</p>

<h3>Mistake: Poor record keeping</h3>
<p><strong>Fix:</strong> Document every interaction. Use a CRM that makes documentation easy.</p>

<h2>When in Doubt</h2>

<p>If you're unsure whether a communication is compliant:</p>
<ul>
<li>Ask your upline/FMO compliance team</li>
<li>Review CMS MCMG guidelines</li>
<li>Err on the side of caution</li>
<li>Document your reasoning</li>
</ul>

<p>Compliance is not optional. Violations can end your Medicare career.</p>
  `,

  faqs: [
    {
      question: "What's the difference between marketing and educational communications?",
      answer: "Marketing communications influence enrollment decisions or promote specific plans—they require beneficiary consent. Educational communications provide general Medicare information without promoting specific plans—they're less restricted. Many retention communications can be framed as educational or service-oriented."
    },
    {
      question: "Do I need a Scope of Appointment for retention calls?",
      answer: "Not for general service calls (checking how coverage is working, answering questions). Yes, if you'll be discussing plan options, comparing plans, or meeting to review enrollment decisions. When in doubt, get an SOA—it protects you."
    },
    {
      question: "What documentation should I keep for compliance?",
      answer: "Date, time, and method of contact; purpose of communication; topics discussed; outcomes and follow-up items; SOA if applicable. Documentation demonstrates compliance if audited and protects you from complaints."
    },
    {
      question: "Can I contact clients during AEP?",
      answer: "Yes, you can conduct annual reviews with existing clients (with SOA), provide plan comparisons, and educate about options. You cannot make unsolicited marketing calls, use high-pressure tactics, or make misleading comparisons. Follow the rules carefully—AEP is strictly enforced."
    },
    {
      question: "How do I make retention outreach compliant?",
      answer: "Focus on service (checking how coverage is working), personal acknowledgments (birthdays), and education (Medicare information). Avoid plan promotion without consent. Document everything. Get SOA before plan discussions. When in doubt, err on the side of caution."
    }
  ],

  sources: [
    {
      title: "Medicare Communications and Marketing Guidelines",
      url: "https://www.cms.gov/Medicare/Health-Plans/ManagedCareMarketing/FinalPartCMarketingGuidelines",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Pro IRP's communication templates are designed with CMS compliance in mind. Outreach that keeps you connected with clients while keeping you on the right side of marketing rules.`
};

export default content;
